import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container, Grid, Link, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SendIcon from '@mui/icons-material/Send';
import { Link as RouterLink } from 'react-router-dom';
import PublicHeader from '../components/Public/PublicHeader';
import PublicFooter from '../components/Public/PublicFooter';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import {
  HoneypotField, REQUEST_CATEGORIES, SUPPORT_EMAIL, fieldErrorsFrom, requesterFromUser, submitErrorMessage,
} from '../components/Support/supportUi';

const emptyForm = { name: '', email: '', company: '', kind: 'support', subject: '', message: '', website: '' };

const SupportPage = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [sentTo, setSentTo] = useState('');

  // Prefill from the signed-in user (the profile may load after first render).
  useEffect(() => {
    if (!user) return;
    const who = requesterFromUser(user);
    setForm((f) => ({ ...f, name: f.name || who.name, email: who.email || f.email, company: f.company || who.company }));
  }, [user]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key]) setFieldErrors((fe) => ({ ...fe, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const local = {};
    if (!form.name.trim()) local.name = 'Name is required';
    if (!form.email.trim()) local.email = 'A valid email is required';
    if (!form.subject.trim()) local.subject = 'Subject is required';
    if (form.message.trim().length < 10) local.message = 'Please describe your request (at least 10 characters)';
    if (Object.keys(local).length) { setFieldErrors(local); return; }
    try {
      setSubmitting(true);
      const payload = {
        kind: form.kind, name: form.name.trim(), email: form.email.trim(), subject: form.subject.trim(), message: form.message.trim(), website: form.website,
      };
      if (form.company.trim()) payload.company = form.company.trim();
      const res = await apiService.support.submit(payload);
      setSentTo(payload.email);
      setTicket(res.data.ticket);
    } catch (err) {
      setFieldErrors(fieldErrorsFrom(err));
      setError(submitErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    const who = requesterFromUser(user);
    setForm({ ...emptyForm, name: who.name, email: who.email, company: who.company });
    setTicket(null);
    setError(null);
    setFieldErrors({});
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <PublicHeader />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flexGrow: 1 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>Support</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 720 }}>
          Questions, problems or ideas — tell us and a person will get back to you. We usually reply within one business day.
          If you are signed in, you can also track your tickets from <strong>Dashboard → Support</strong>.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {ticket ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Request received</Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>Ticket <Box component="span" sx={{ fontFamily: 'monospace' }}>{ticket.number}</Box></Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    We&apos;ve emailed a confirmation to <strong>{sentTo}</strong>. Reply to that email to add more information.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
                    <Button variant="contained" onClick={reset}>Submit another</Button>
                    {user && <Button component={RouterLink} to="/dashboard/support" variant="outlined">View my tickets</Button>}
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent component="form" noValidate onSubmit={submit} sx={{ position: 'relative' }}>
                  {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth required label="Name" value={form.name} onChange={set('name')} error={Boolean(fieldErrors.name)} helperText={fieldErrors.name} autoComplete="name" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth required type="email" label="Email" value={form.email} onChange={set('email')}
                        error={Boolean(fieldErrors.email)} helperText={fieldErrors.email || (user ? 'Replies go to your account email' : '')}
                        InputProps={{ readOnly: Boolean(user) }} autoComplete="email"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Company (optional)" value={form.company} onChange={set('company')} error={Boolean(fieldErrors.company)} helperText={fieldErrors.company} autoComplete="organization" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Category" value={form.kind} onChange={set('kind')}>
                        {REQUEST_CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth required label="Subject" value={form.subject} onChange={set('subject')} error={Boolean(fieldErrors.subject)} helperText={fieldErrors.subject} inputProps={{ maxLength: 200 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth required multiline minRows={6} label="Message" value={form.message} onChange={set('message')}
                        error={Boolean(fieldErrors.message)} helperText={fieldErrors.message || 'Include steps to reproduce, screenshots links or error messages where relevant.'}
                      />
                    </Grid>
                  </Grid>
                  <HoneypotField value={form.website} onChange={(v) => setForm((f) => ({ ...f, website: v }))} />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="submit" variant="contained" size="large" disabled={submitting} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}>
                      Send request
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <EmailIcon color="primary" />
                    <Typography variant="h6">Email us directly</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Prefer email? Write to us and it lands in the same queue.
                  </Typography>
                  <Link href={`mailto:${SUPPORT_EMAIL}`} variant="body1" sx={{ fontWeight: 600 }}>{SUPPORT_EMAIL}</Link>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <HandshakeIcon color="warning" />
                    <Typography variant="h6">Partner with us</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Run a SOC or MSSP and want integrations built for your stack? Join our design partner programme.
                  </Typography>
                  <Button component={RouterLink} to="/partner" variant="outlined" size="small">Learn more</Button>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
      <PublicFooter />
    </Box>
  );
};

export default SupportPage;
